import {useCallback, useMemo, useRef, useState} from 'react'

/**
 * A map of values keyed by string (one per chat tab), readable synchronously outside render.
 *
 * The ref is the source of truth so a session controller can read the value for its own
 * conversation the moment a turn starts; the version bump only re-renders the owner.
 */
export function useKeyedState<T>() {
  const storeRef = useRef<Readonly<Record<string, T>>>({})
  const [, setVersion] = useState(0)

  const commit = useCallback((next: Readonly<Record<string, T>>) => {
    storeRef.current = next
    setVersion((v) => v + 1)
  }, [])

  const get = useCallback((key: string): T | undefined => storeRef.current[key], [])

  /** Replace one key's value; returning `undefined` removes the key. */
  const update = useCallback(
    (key: string, fn: (prev: T | undefined) => T | undefined) => {
      const {[key]: prev, ...rest} = storeRef.current
      const next = fn(prev)
      commit(next === undefined ? rest : {...rest, [key]: next})
    },
    [commit],
  )

  /** Hand `from`'s value to `to` (only when `to` has none yet), removing `from`. */
  const move = useCallback(
    (from: string, to: string) => {
      const {[from]: value, ...rest} = storeRef.current
      if (value === undefined || from === to || to in rest) return
      commit({...rest, [to]: value})
    },
    [commit],
  )

  /** Drop every key not in `keep` (closed tabs). */
  const retain = useCallback(
    (keep: readonly string[]) => {
      const current = storeRef.current
      const kept = Object.fromEntries(Object.entries(current).filter(([key]) => keep.includes(key)))
      if (Object.keys(kept).length !== Object.keys(current).length) {
        commit(kept)
      }
    },
    [commit],
  )

  return useMemo(() => ({get, update, move, retain}), [get, update, move, retain])
}
